import {
  CustomFieldDefinitionEntityTypeModel,
  FieldConstraintDefinitionModel,
  FieldKeyModel,
  PermissionName
} from '@utmost.co/enterprise-models';
import React, { useContext } from 'react';
import { UserEventTracker } from '../../integrations/userEvents/UserEventsTypes';
import type { ElementWithMeta } from '../../types';
import { SmartFormFeatureFlags } from '../SmartFormFeatures.types';
import { FieldValidatorInterface } from './FieldValidatorInterface';

export type FieldPermission = {
  READ?: boolean;
  WRITE?: boolean;
  PERMISSION_NAME?: PermissionName;
};

export interface FormControllerFieldMeta {
  [name: string]: {
    disabled: boolean;
    permissions?: FieldPermission;
    /*! matchHint - Optional consumer-supplied node rendered below this field's own control, regardless of which
     * component renders the field. Used to surface external record-matching hints (e.g. "Matches existing X",
     * "Auto-completed") without needing to know or alter each field component's internals. Distinct from
     * SmartformFieldConfig's `hint` (the customer-configured field hint/placeholder). */
    matchHint?: React.ReactNode;
    /*! matchHighlighted - Optional flag paired with matchHint: when true, the field's own input control (not just
     * the hint text below it) is rendered with a highlighted background, e.g. to mark a field as auto-completed
     * from an external record match. */
    matchHighlighted?: boolean;
  };
}

export type SmartformTranslations = {
  submitLabel: string;
  loadingLabel: string;
  startTypingLabel: string;
  loading: string;
  docNotSupported: string;
  typeNotSupported: (type?: any) => string;
  noMatchesLabel: (matchText: any) => string;
  moreResultsLabel: string;
  noOptionsLabel: string;
  genderMaleLabel: string;
  genderFemaleLabel: string;
  genderOtherLabel: string;
  genderUnknownLabel: string;
  selectPhoneNumRowLabel: ({ rowIndex }: { rowIndex: number }) => string;
  labelPrimaryLabel: string;
  invalidDateLabel: string;
  hiddenFieldHasErrors: ({ name, b }: { name: string; b: (value: string[]) => JSX.Element }) => string;
};

export interface SmartformContextProps {
  readonly?: boolean;
  permissionDebugger?: boolean;
  targetEntityUid?: string;
  /*! requestHeaders - Optional headers that field components send with the requests they make (e.g. option lookups). */
  requestHeaders?: Record<string, string>;
  parentEntityUids?: Map<CustomFieldDefinitionEntityTypeModel, string>;
  fieldMeta?: FormControllerFieldMeta;
  translations?: SmartformTranslations;
  previewMode?: boolean;
  validator?: FieldValidatorInterface;
  constraintsDefinitions?: Pick<
    FieldConstraintDefinitionModel,
    | 'uid'
    | 'constrainedByCustomFieldReferenceId'
    | 'constrainedByFieldKey'
    | 'customFieldReferenceId'
    | 'entityType'
    | 'referenceId'
    | 'fieldKey'
  >[];
  contextMap?: Map<string, object>;
  featureFlags?: SmartFormFeatureFlags;
  onTrackUserEvent?: UserEventTracker;
  elements?: ElementWithMeta[];
  /*! onFieldBlur - same callback passed to Smartform's `onFieldBlur` prop, exposed via context so elements can
   * report changes that aren't a plain field blur (e.g. removing a row from a FieldArray) using the same
   * (fieldKey, value, path) signature. Consumers that don't care about a given fieldKey are expected to no-op. */
  onFieldBlur?: (fieldKey: FieldKeyModel, value: any, path: string) => void;
}

export const SmartformContext = React.createContext<Partial<SmartformContextProps>>({});
export const SmartformProvider = SmartformContext.Provider;
export function useSmartformContext() {
  return useContext<Partial<SmartformContextProps>>(SmartformContext);
}
